import { FC, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { downloadLink, getPrettyDate } from 'utils/util/util';
import Loading from 'views/Widgets/Loading';
import FilterablePeopleTable from 'views/Widgets/People/FilterablePeopleTable';

interface MediaHistoryItem {
  id: number;
  file: string;
  created_at: string;
}

interface Document {
  id: number;
  name: string;
  media_history: MediaHistoryItem[];
}

interface MediaHistoryDocumentModalProps {
  isOpen: boolean;
  toggle: () => void;
  documentId: number;
}

const MediaHistoryDocumentModal: FC<MediaHistoryDocumentModalProps> = ({
  isOpen,
  toggle,
  documentId,
}) => {
  const { formatMessage, locale } = useIntl();

  const { data: document, status } = useConfirmApi<Document>({
    url: `documents/${documentId}`,
    method: 'GET',
    disabled: !isOpen,
  });

  const handleDownload = useCallback((file: string) => {
    downloadLink({
      filename: file.split('/').pop() || 'document',
      url: file,
    });
  }, []);

  const mediaHistory = useMemo(
    () => document?.media_history || [],
    [document?.media_history]
  );

  const columns = useMemo(() => {
    return [
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.media_history_modal.table.version',
          defaultMessage: 'Version',
        }),
        field: 'version',
        className: 'col-1',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.media_history_modal.table.file',
          defaultMessage: 'File',
        }),
        field: 'filename',
        className: 'col-4',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.media_history_modal.table.created_at',
          defaultMessage: 'Created At',
        }),
        field: 'created_at',
        format: (date: string) => {
          return getPrettyDate({
            dateString: date,
            includeTime: true,
            locale,
          });
        },
        className: 'col-3',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.media_history_modal.table.actions',
          defaultMessage: 'Actions',
        }),
        field: 'actions',
        className: 'col-2',
        sortable: false,
      },
    ];
  }, [formatMessage, locale]);

  const rows = useMemo(() => {
    return mediaHistory.map((media, index) => ({
      id: media.id,
      version: mediaHistory.length - index,
      filename: media.file.split('/').pop(),
      created_at: media.created_at,
      actions: (
        <Button
          color="link"
          size="sm"
          onClick={() => handleDownload(media.file)}
          className="p-0"
        >
          <i className="fe fe-download me-1" />
          <FormattedMessage
            id="app.views.administration.content_administration.documents_admin.media_history_modal.download"
            defaultMessage="Download"
          />
        </Button>
      ),
    }));
  }, [mediaHistory, handleDownload]);

  if (status === 'LOADING' || !document) {
    return (
      <Loading
        layout="overlay"
        message={formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.media_history_modal.loading',
          defaultMessage: 'Loading document history...',
        })}
      />
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <FormattedMessage
          id="app.views.administration.content_administration.documents_admin.media_history_modal.title"
          defaultMessage="Version History - {documentName}"
          values={{ documentName: document.name }}
        />
      </ModalHeader>
      <ModalBody>
        {mediaHistory.length === 0 ? (
          <p className="text-muted">
            <FormattedMessage
              id="app.views.administration.content_administration.documents_admin.media_history_modal.no_history"
              defaultMessage="No version history available for this document."
            />
          </p>
        ) : (
          <FilterablePeopleTable
            columns={columns}
            rows={rows}
            hideFilters={true}
            hideExportButton={true}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          <FormattedMessage
            id="app.views.administration.content_administration.documents_admin.media_history_modal.close"
            defaultMessage="Close"
          />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default MediaHistoryDocumentModal;
